import type { SnoozePreset } from '@hugo/core'
import { useNavigate } from '@tanstack/react-router'
import { defaultFilter } from 'cmdk'
import {
  AlarmClockIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarPlusIcon,
  CheckIcon,
  ClockIcon,
  ListTodoIcon,
  LogOutIcon,
  MegaphoneIcon,
  MicIcon,
  MonitorIcon,
  MoonIcon,
  PanelRightOpenIcon,
  PlusIcon,
  SparklesIcon,
  SunIcon,
  Trash2Icon,
  Undo2Icon,
  UserRoundCheckIcon,
  XIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'
import { choiceLabel, kindLabel } from '@/lib/format'
import { useItemActions } from '@/lib/item-actions'
import { useCounts, useLastChange, useRunTool } from '@/lib/items'
import { WAITING_CHOICES } from '@/lib/lists'
import { PRIORITY_CHOICES } from '@/lib/priority'
import { useSession } from '@/lib/session'
import { type Theme, useTheme } from '@/lib/theme'
import { useVoiceAvailable } from '@/lib/voice/available'
import { allNav } from './nav'
import { useWorkspace } from './workspace'

/**
 * "Capture …" is the fallback: it always matches but ranks below real commands. cmdk sorts items
 * by score but does not reorder groups with headings, so the group is also rendered last.
 */
function paletteFilter(value: string, search: string, keywords?: string[]): number {
  if (value.startsWith('capture ')) return search.trim() ? 0.0001 : 0
  return defaultFilter(value, search, keywords)
}

const SNOOZES: { preset: SnoozePreset; label: string }[] = [
  { preset: 'tomorrow', label: 'Snooze until tomorrow' },
  { preset: 'in_2_days', label: 'Snooze for 2 days' },
  { preset: 'next_week', label: 'Snooze until next week' },
  { preset: 'later_today', label: 'Snooze for 3 hours' },
]

/** ⌘K / Ctrl+K: go anywhere, act on the selected item, capture, undo. */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const { signOut } = useSession()
  const { setTheme } = useTheme()
  const { selected, openDetails, setCaptureOpen, openDictation } = useWorkspace()
  const voice = useVoiceAvailable()
  const actions = useItemActions('palette')
  const { run } = useRunTool()
  const lastChange = useLastChange()
  const counts = useCounts()
  const toSend = (counts.data?.headsUps ?? 0) + (counts.data?.reminders ?? 0)
  const [search, setSearch] = useState('')

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const exec = (action: () => unknown) => {
    onOpenChange(false)
    void action()
  }

  const themes: { theme: Theme; label: string; icon: typeof SunIcon }[] = [
    { theme: 'light', label: 'Light theme', icon: SunIcon },
    { theme: 'dark', label: 'Dark theme', icon: MoonIcon },
    { theme: 'system', label: 'System theme', icon: MonitorIcon },
  ]
  const item = selected
  const openItem = item && item.status !== 'done' && item.status !== 'dropped'
  const suggestion = item?.status === 'inbox' ? item.suggestion : null
  const captureText = search.trim()

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Command palette"
      description="Search for a page or an action"
    >
      <Command loop filter={paletteFilter}>
        <CommandInput
          placeholder="Type a command, or text to capture…"
          aria-label="Command"
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          <CommandEmpty>No results.</CommandEmpty>
          {item && openItem ? (
            <CommandGroup heading={`Selected: ${item.title}`}>
              {suggestion ? (
                <>
                  <CommandItem
                    value="Accept hugo's suggestion"
                    keywords={[choiceLabel(suggestion.choice), 'ai']}
                    onSelect={() => exec(() => actions.accept(item))}
                  >
                    <SparklesIcon aria-hidden="true" />
                    <span className="truncate">
                      Accept hugo's suggestion: {choiceLabel(suggestion.choice)}
                    </span>
                    <CommandShortcut>A</CommandShortcut>
                  </CommandItem>
                  <CommandItem
                    value="Dismiss suggestion"
                    keywords={['ai']}
                    onSelect={() => exec(() => actions.dismissSuggestion(item))}
                  >
                    <XIcon aria-hidden="true" />
                    Dismiss suggestion
                    <CommandShortcut>D</CommandShortcut>
                  </CommandItem>
                </>
              ) : null}
              <CommandItem value="Mark done" onSelect={() => exec(() => actions.complete(item))}>
                <CheckIcon aria-hidden="true" />
                Mark done
                <CommandShortcut>E</CommandShortcut>
              </CommandItem>
              {SNOOZES.map((s) => (
                <CommandItem
                  key={s.preset}
                  value={s.label}
                  onSelect={() => exec(() => actions.snooze(item, s.preset))}
                >
                  <AlarmClockIcon aria-hidden="true" />
                  {s.label}
                </CommandItem>
              ))}
              {WAITING_CHOICES.filter(
                (choice) => item.status === 'inbox' || item.kind !== choice.kind,
              ).map((choice) => {
                // An inbox item is filed; a filed one changes who waits on it (one list).
                const text =
                  item.status === 'inbox'
                    ? `File in ${kindLabel(choice.kind)}`
                    : `Waiting: ${choice.label.toLowerCase()}`
                return (
                  <CommandItem
                    key={choice.kind}
                    value={text}
                    keywords={[kindLabel(choice.kind), 'move', 'mark']}
                    onSelect={() => exec(() => actions.move(item, choice.kind))}
                  >
                    {choice.kind === 'task' ? (
                      <ListTodoIcon aria-hidden="true" />
                    ) : choice.kind === 'owe' ? (
                      <UserRoundCheckIcon aria-hidden="true" />
                    ) : (
                      <ClockIcon aria-hidden="true" />
                    )}
                    {text}
                    {item.status !== 'inbox' && choice.kind !== 'task' ? (
                      <span className="text-muted-foreground">· {kindLabel(choice.kind)}</span>
                    ) : null}
                  </CommandItem>
                )
              })}
              {item.status !== 'inbox' && (item.kind === 'task' || item.kind === 'owe')
                ? PRIORITY_CHOICES.filter((choice) => choice.value !== item.priority).map(
                    (choice) => (
                      <CommandItem
                        key={choice.value}
                        value={`Priority: ${choice.label}`}
                        keywords={['priority', 'urgent', 'important']}
                        onSelect={() => exec(() => actions.setPriority(item, choice.value))}
                      >
                        {choice.value === 'low' ? (
                          <ArrowDownIcon aria-hidden="true" />
                        ) : (
                          <ArrowUpIcon aria-hidden="true" />
                        )}
                        Priority: {choice.label}
                      </CommandItem>
                    ),
                  )
                : null}
              <CommandItem value="Open details" onSelect={() => exec(() => openDetails(item.id))}>
                <PanelRightOpenIcon aria-hidden="true" />
                Open details
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
              <CommandItem
                value="Drop"
                onSelect={() =>
                  exec(() =>
                    item.status === 'inbox' ? actions.triage(item, 'ignore') : actions.drop(item),
                  )
                }
              >
                <Trash2Icon aria-hidden="true" />
                {item.status === 'inbox' ? 'Ignore' : 'Drop'}
                <CommandShortcut>X</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          ) : null}
          <CommandGroup heading="Actions">
            <CommandItem value="New item capture" onSelect={() => exec(() => setCaptureOpen(true))}>
              <PlusIcon aria-hidden="true" />
              New item…
              <CommandShortcut>C</CommandShortcut>
            </CommandItem>
            {voice ? (
              <CommandItem
                value="Dictate a new item"
                keywords={['voice', 'speak', 'say', 'microphone', 'mic']}
                onSelect={() => exec(() => openDictation())}
              >
                <MicIcon aria-hidden="true" />
                Dictate a new item…
                <CommandShortcut>V</CommandShortcut>
              </CommandItem>
            ) : null}
            <CommandItem
              value="Heads-ups and reminders to send"
              // Not "outbox": typing that must still pick the Outbox itself.
              keywords={['tell', 'late', 'delay', 'away', 'remind', 'follow up', 'nudge', 'chase']}
              onSelect={() => exec(() => navigate({ to: '/outbox' }))}
            >
              <MegaphoneIcon aria-hidden="true" />
              Heads-ups and reminders to send
              {toSend > 0 ? (
                <span className="ml-auto text-xs text-muted-foreground tabular-nums">{toSend}</span>
              ) : null}
            </CommandItem>
            <CommandItem
              value="Add time off"
              keywords={['vacation', 'holiday', 'away', 'absence', 'days off']}
              onSelect={() =>
                exec(() =>
                  navigate({
                    to: '/settings/$section',
                    params: { section: 'time-off' },
                    hash: 'add-time-off',
                  }),
                )
              }
            >
              <CalendarPlusIcon aria-hidden="true" />
              Add time off
            </CommandItem>
            {lastChange ? (
              <CommandItem
                value={`Undo ${lastChange.label}`}
                onSelect={() => exec(() => actions.undo(lastChange.actionId))}
              >
                <Undo2Icon aria-hidden="true" />
                Undo: {lastChange.label}
                <CommandShortcut>Z</CommandShortcut>
              </CommandItem>
            ) : null}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Go to">
            {allNav.map((nav) => (
              <CommandItem
                key={nav.to}
                value={`go ${nav.label}`}
                onSelect={() => exec(() => navigate({ to: nav.to }))}
              >
                <nav.icon aria-hidden="true" />
                {nav.label}
                <CommandShortcut>G {nav.key.toUpperCase()}</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Preferences">
            {themes.map(({ theme, label, icon: Icon }) => (
              <CommandItem key={theme} value={label} onSelect={() => exec(() => setTheme(theme))}>
                <Icon aria-hidden="true" />
                {label}
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Account">
            <CommandItem value="Sign out" onSelect={() => exec(signOut)}>
              <LogOutIcon aria-hidden="true" />
              Sign out
            </CommandItem>
          </CommandGroup>
          {captureText ? (
            <CommandGroup heading="Capture">
              <CommandItem
                value={`capture ${captureText}`}
                keywords={[captureText]}
                onSelect={() =>
                  exec(() =>
                    run(
                      'item.capture',
                      { text: captureText },
                      {
                        via: 'palette',
                        done: (r) =>
                          `Added to ${kindLabel(r.item.kind)}${r.item.counterpart ? ` · ${r.item.counterpart.name}` : ''}`,
                      },
                    ),
                  )
                }
              >
                <PlusIcon aria-hidden="true" />
                <span className="truncate">Capture “{captureText}”</span>
              </CommandItem>
            </CommandGroup>
          ) : null}
        </CommandList>
      </Command>
    </CommandDialog>
  )
}
